import React from 'react';
import './Pages.css';

export default function PrivacyPage() {
  return (
    <div className="static-page fade-in">
      <div className="static-card card">
        <h1>Privacy Policy &amp; GDPR</h1>
        <p className="static-updated">Last updated: April 2026</p>

        <section className="static-section">
          <h2>1. Introduction</h2>
          <p>
            Crunchy ("we", "us", "our") is committed to protecting your personal data and respecting your privacy. This Privacy Policy explains how we collect, use, store, and share your information when you use our platform. It also outlines your rights under the General Data Protection Regulation (GDPR) and other applicable data protection laws.
          </p>
        </section>

        <section className="static-section">
          <h2>2. Data We Collect</h2>
          <p>We collect the following types of personal data:</p>
          <ul>
            <li><strong>Account information:</strong> username, email address, and encrypted password when you register.</li>
            <li><strong>Profile data:</strong> profile picture, biography, birthday, and food preferences that you choose to provide.</li>
            <li><strong>Content data:</strong> recipes, comments, messages, and images you upload to the platform.</li>
            <li><strong>Usage data:</strong> pages visited, features used, and interactions with other users, collected to improve the Service.</li>
            <li><strong>Device data:</strong> browser type, operating system, and IP address collected automatically through server logs.</li>
          </ul>
        </section>

        <section className="static-section">
          <h2>3. How We Use Your Data</h2>
          <p>We use your personal data to:</p>
          <ul>
            <li>Provide, operate, and maintain the Crunchy platform.</li>
            <li>Personalise your experience, including recipe recommendations based on your food preferences.</li>
            <li>Enable communication between users through the messaging system.</li>
            <li>Send service-related notifications (e.g. password resets) when you have email notifications enabled.</li>
            <li>Monitor and analyse usage patterns to improve the Service.</li>
            <li>Detect, prevent, and address technical issues and security threats.</li>
          </ul>
        </section>

        <section className="static-section">
          <h2>4. Legal Basis for Processing (GDPR)</h2>
          <p>Under the GDPR, we process your personal data on the following legal bases:</p>
          <ul>
            <li><strong>Consent:</strong> when you create an account and agree to our terms, and when you opt in to email notifications.</li>
            <li><strong>Contractual necessity:</strong> processing required to deliver the Service you have signed up for.</li>
            <li><strong>Legitimate interests:</strong> improving our platform, ensuring security, and preventing abuse — provided these interests are not overridden by your rights.</li>
          </ul>
        </section>

        <section className="static-section">
          <h2>5. Data Sharing</h2>
          <p>
            We do not sell your personal data. We may share your data with third-party service providers solely for the purpose of operating the Service, including cloud hosting (Google Cloud Platform), image storage (Firebase Storage), and email delivery services. All third-party providers are contractually obligated to handle your data securely and only as instructed by us.
          </p>
        </section>

        <section className="static-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal data for as long as your account is active or as needed to provide the Service. If you delete your account, we will delete or anonymise your personal data within 30 days, except where we are legally required to retain certain records. Recipe content you have published may remain visible on the platform in anonymised form after account deletion.
          </p>
        </section>

        <section className="static-section">
          <h2>7. Your Rights Under GDPR</h2>
          <p>If you are located in the European Economic Area (EEA) or the United Kingdom, you have the following rights:</p>
          <ul>
            <li><strong>Right of access:</strong> request a copy of the personal data we hold about you.</li>
            <li><strong>Right to rectification:</strong> request correction of inaccurate or incomplete data.</li>
            <li><strong>Right to erasure:</strong> request deletion of your personal data ("right to be forgotten").</li>
            <li><strong>Right to restriction:</strong> request that we limit how we process your data.</li>
            <li><strong>Right to data portability:</strong> receive your data in a structured, machine-readable format.</li>
            <li><strong>Right to object:</strong> object to processing based on legitimate interests or direct marketing.</li>
            <li><strong>Right to withdraw consent:</strong> withdraw consent at any time where processing is based on consent.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:support@crunchyrecipes.co.uk">support@crunchyrecipes.co.uk</a>. We will respond within 30 days.
          </p>
        </section>

        <section className="static-section">
          <h2>8. Cookies</h2>
          <p>
            Crunchy uses essential cookies and local storage to maintain your login session and preferences. We do not use third-party tracking cookies or advertising cookies. No cookie consent banner is required as we only use strictly necessary cookies.
          </p>
        </section>

        <section className="static-section">
          <h2>9. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your data, including encrypted password storage (bcrypt), HTTPS encryption for all data in transit, and access controls on our cloud infrastructure. While no system is completely secure, we are committed to protecting your information to the best of our ability.
          </p>
        </section>

        <section className="static-section">
          <h2>10. Children's Privacy</h2>
          <p>
            The Service is not directed at children under 13. We do not knowingly collect personal data from children under 13. If we become aware that a child under 13 has provided us with personal information, we will take steps to delete that information promptly.
          </p>
        </section>

        <section className="static-section">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify registered users of significant changes via email or an in-app notification. Continued use of the Service after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="static-section">
          <h2>12. Contact</h2>
          <p>
            For privacy-related enquiries or to exercise your data protection rights, please contact us at{' '}
            <a href="mailto:support@crunchyrecipes.co.uk">support@crunchyrecipes.co.uk</a>.
          </p>
        </section>
      </div>
    </div>
  );
}
